import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import { useBranches } from '../../hooks/useBranches';
import { useKeyboardForm } from '../../hooks/useKeyboardForm';
import { usePermission } from '../../hooks/usePermission';
import { date, fine, money, today } from '../../utils/format';
import { Empty, ErrorNotice, Loading, PageHeader, Table } from '../../components/ui';
import { DateInput, Drawer, Field, Select, TextInput } from '../../components/ui/Form';

interface AdjustmentRow {
  id: string;
  doc_no: string;
  doc_date: string;
  reason_code: string;
  reason: string;
  direction: string;
  total_fine_wt: string;
  value_impact: string;
  status: string;
  branch_name: string;
}

interface Item {
  id: string;
  item_code: string;
  product_id: string;
  purity_id: string;
  product_name: string;
  purity_code: string;
  fine_wt: string;
}

const REASONS = [
  { value: 'DAMAGE', label: 'Damaged' },
  { value: 'LOSS', label: 'Lost' },
  { value: 'FOUND', label: 'Found' },
  { value: 'WEIGHING_ERROR', label: 'Weighing error' },
  { value: 'OTHER', label: 'Other' },
];

/**
 * Writing stock off, or on.
 *
 * Raised by one person and approved by another — enforced by the database, not
 * only by this screen. A write-off goes to an expense account where it shows in
 * the P&L, rather than the stock quietly changing.
 */
export function AdjustmentsPage() {
  const { can } = usePermission();
  const client = useQueryClient();
  const onEnter = useKeyboardForm();
  const { branches, defaultBranchId } = useBranches();

  const [open, setOpen] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [form, setForm] = useState({ reasonCode: 'DAMAGE', reason: '', docDate: today(), branchId: '' });
  const [itemId, setItemId] = useState('');
  const [newStatus, setNewStatus] = useState('DAMAGED');

  const list = useQuery({
    queryKey: ['adjustments'],
    queryFn: () => api.get<{ data: AdjustmentRow[] }>('/adjustments'),
  });
  const items = useQuery({
    queryKey: ['items', 'IN_STOCK', form.branchId],
    queryFn: () =>
      endpoints.inventory.items({ status: 'IN_STOCK', branchId: form.branchId || undefined, limit: '200' }),
    enabled: open,
  });

  const itemList = (items.data?.data ?? []) as unknown as Item[];
  const chosen = useMemo(() => itemList.find((i) => i.id === itemId), [itemList, itemId]);

  const create = useMutation({
    mutationFn: () =>
      api.post(
        '/adjustments',
        {
          branchId: form.branchId || defaultBranchId,
          docDate: form.docDate,
          reasonCode: form.reasonCode,
          reason: form.reason,
          lines: [
            {
              stockItemId: chosen!.id,
              productId: chosen!.product_id,
              purityId: chosen!.purity_id,
              // Writing a piece off removes all of its fine weight.
              adjustFineWt: `-${chosen!.fine_wt}`,
              newStatus,
            },
          ],
        },
        { 'idempotency-key': crypto.randomUUID() },
      ),
    onSuccess: () => {
      setOpen(false);
      setItemId('');
      setForm({ ...form, reason: '' });
      setError(null);
      void client.invalidateQueries({ queryKey: ['adjustments'] });
      void client.invalidateQueries({ queryKey: ['items'] });
    },
    onError: setError,
  });

  const approve = useMutation({
    mutationFn: (id: string) => api.post(`/adjustments/${id}/approve`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['adjustments'] });
      void client.invalidateQueries({ queryKey: ['items'] });
    },
    onError: setError,
  });

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    create.mutate();
  };

  const rows = list.data?.data ?? [];

  return (
    <>
      <PageHeader
        title="Stock adjustments"
        subtitle="Approved by the owner or a company admin, and never by whoever raised it — the database enforces that, not just this screen."
        actions={
          can('stock.adjust_request') ? (
            <button className="btn-primary" onClick={() => setOpen(true)}>
              Raise adjustment
            </button>
          ) : null
        }
      />
      <ErrorNotice error={error} />

      {list.isLoading ? <Loading /> : null}
      {!list.isLoading && rows.length === 0 ? <Empty
          message="No adjustments raised."
          hint="Stock only changes here with a reason and a second person's approval."
          action={can('stock.adjust_request') ? { label: 'Raise adjustment', onClick: () => setOpen(true) } : undefined}
        /> : null}

      {rows.length > 0 ? (
        <Table head={['Date', 'Document', 'Branch', 'Reason', 'Explanation', 'Fine wt', 'Value', 'Status', '']}>
          {rows.map((a) => (
            <tr key={a.id}>
              <td className="td">{date(a.doc_date)}</td>
              <td className="td num">{a.doc_no}</td>
              <td className="td">{a.branch_name}</td>
              <td className="td text-xs">{a.reason_code.replace(/_/g, ' ').toLowerCase()}</td>
              <td className="td text-xs">{a.reason}</td>
              <td className="td num text-right">{fine(a.total_fine_wt)}</td>
              <td className="td num text-right">
                <span className={Number(a.value_impact) < 0 ? 'text-danger' : 'text-success'}>
                  {money(a.value_impact)}
                </span>
              </td>
              <td className="td text-xs">{a.status.toLowerCase()}</td>
              <td className="td">
                {a.status === 'REQUESTED' && can('stock.adjust_approve') ? (
                  <button className="btn-ghost py-1 text-xs" onClick={() => approve.mutate(a.id)}>
                    Approve
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
        </Table>
      ) : null}

      {open ? (
        <Drawer
          title="Raise an adjustment"
          subtitle="It does not touch stock until somebody else approves it."
          onClose={() => setOpen(false)}
        >
          <ErrorNotice error={error} />
          <form autoComplete="off" onKeyDown={onEnter} onSubmit={submit} className="space-y-4">
            {branches.length > 1 ? (
              <Field label="Branch" required>
                <Select
                  value={form.branchId || (defaultBranchId ?? '')}
                  onChange={(v) => {
                    setForm({ ...form, branchId: v });
                    setItemId('');
                  }}
                  options={branches.map((b) => ({ value: b.id, label: b.name }))}
                />
              </Field>
            ) : null}

            <Field label="Piece" required>
              <Select
                value={itemId}
                onChange={setItemId}
                options={itemList.map((i) => ({
                  value: i.id,
                  label: `${i.item_code} — ${i.product_name} (${i.fine_wt} fine)`,
                }))}
                placeholder={itemList.length === 0 ? 'Nothing in stock here' : 'Choose…'}
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Reason" required>
                <Select
                  value={form.reasonCode}
                  onChange={(v) => setForm({ ...form, reasonCode: v })}
                  options={REASONS}
                />
              </Field>
              <Field label="Mark the piece as" required>
                <Select
                  value={newStatus}
                  onChange={setNewStatus}
                  options={[
                    { value: 'DAMAGED', label: 'Damaged' },
                    { value: 'LOST', label: 'Lost' },
                    { value: 'SCRAPPED', label: 'Scrapped' },
                  ]}
                />
              </Field>
            </div>

            <Field label="Date" required>
              <DateInput value={form.docDate} onChange={(v) => setForm({ ...form, docDate: v })} />
            </Field>

            <Field
              label="What happened"
              required
              hint="Kept permanently. Stock that changes without an explanation is stock nobody can account for."
            >
              <TextInput
                value={form.reason}
                onChange={(v) => setForm({ ...form, reason: v })}
                placeholder="Clasp broken in the display case"
              />
            </Field>

            {chosen ? (
              <p className="rounded border border-ink/15 bg-white p-2 text-xs">
                Writing off <span className="num">{fine(chosen.fine_wt)}</span> of {chosen.purity_code}. The value
                comes off stock and lands in the P&amp;L as a loss.
              </p>
            ) : null}

            <button
              className="btn-primary w-full"
              disabled={!itemId || form.reason.trim().length < 3 || create.isPending}
            >
              {create.isPending ? 'Raising…' : 'Raise adjustment'}
            </button>
          </form>
        </Drawer>
      ) : null}
    </>
  );
}
